import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Search, X } from 'lucide-react';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { formatCurrency } from '../../data/mockData';
import { lookupBarcodeEnrich } from '../../api/inventory';
import { SELLING_PRICE_MRP_MESSAGE, isSellingBelowMrp } from '../../utils/posMoney';
import styles from './InstantOnboardModal.module.css';

function formatEnrichSource(source) {
  if (!source || source === 'catalog') return 'catalog';
  return String(source).replace(/_/g, ' ');
}

export default function InstantOnboardModal({
  mode = 'EACH',
  initialBarcode = '',
  token,
  products = [],
  onClose,
  onCreate,
  onUseExisting,
}) {
  const isWeight = mode === 'WEIGHT';
  const [barcode, setBarcode] = useState(initialBarcode);
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [qty, setQty] = useState(isWeight ? '' : '1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [enrichNotice, setEnrichNotice] = useState(null);
  const [existingMatch, setExistingMatch] = useState(null);
  const lastLookupRef = useRef('');
  const nameInputRef = useRef(null);

  const focusNameInput = () => {
    requestAnimationFrame(() => nameInputRef.current?.focus());
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(name.trim().toLowerCase()), 250);
    return () => clearTimeout(timer);
  }, [name]);

  // After a barcode scan opens this modal, land on product name for quick entry.
  useEffect(() => {
    if (initialBarcode) focusNameInput();
  }, [initialBarcode]);

  const matches = useMemo(() => {
    if (debouncedName.length < 2) return [];
    return products
      .filter((product) => product.status === 'ACTIVE')
      .filter((product) => (isWeight ? product.soldBy === 'WEIGHT' : product.soldBy !== 'WEIGHT'))
      // Avoid duplicating the barcode "Already in catalog" hit under Existing matches.
      .filter((product) => !existingMatch || product.id !== existingMatch.id)
      .filter((product) => product.name.toLowerCase().includes(debouncedName))
      .slice(0, 5);
  }, [debouncedName, existingMatch, isWeight, products]);

  const applySuggestion = (suggestion) => {
    if (!suggestion) return;
    if (suggestion.name) setName(suggestion.name);
    if (suggestion.image_url) setImageUrl(suggestion.image_url);
  };

  const runBarcodeLookup = async (rawBarcode) => {
    if (isWeight) return;
    const code = String(rawBarcode || '').trim();
    if (!code || !token) return;
    if (code === lastLookupRef.current) return;
    if (!/^\d+$/.test(code)) {
      setEnrichNotice({ tone: 'warning', text: 'Barcode must be digits only (EAN-13 / UPC).' });
      return;
    }

    setEnriching(true);
    setEnrichNotice(null);
    setExistingMatch(null);
    lastLookupRef.current = code;

    try {
      const data = await lookupBarcodeEnrich(code, token);

      if (data.already_onboarded && data.item_id) {
        if (data.suggestion) applySuggestion(data.suggestion);
        const existing = products.find((product) => product.id === data.item_id) || null;
        setExistingMatch(existing);
        setEnrichNotice({
          tone: 'warning',
          text: existing
            ? `Already onboarded — ${existing.name}. Use existing to add it to this sale.`
            : `Already onboarded (${data.suggestion?.name || code}). Creating will reuse the catalog item.`,
        });
        return;
      }

      if (data.found && data.suggestion) {
        applySuggestion(data.suggestion);
        setEnrichNotice({
          tone: 'success',
          text: `Prefilled from ${formatEnrichSource(data.source)}. Check prices before adding.`,
        });
        return;
      }

      setEnrichNotice({
        tone: 'info',
        text: 'Not found in external databases — enter product details manually.',
      });
    } catch (err) {
      if (err.message === 'forbidden') {
        lastLookupRef.current = '';
        setEnrichNotice(null);
        return;
      }
      if (err.message === 'invalid_barcode' || err.message === 'barcode_required') {
        setEnrichNotice({ tone: 'warning', text: 'Invalid barcode — use digits only.' });
        return;
      }
      if (err.name === 'AbortError' || err.message === 'timeout') {
        setEnrichNotice({
          tone: 'info',
          text: 'Lookup timed out — enter product details manually.',
        });
        return;
      }
      setEnrichNotice({
        tone: 'info',
        text: 'Could not enrich barcode — enter product details manually.',
      });
    } finally {
      setEnriching(false);
      focusNameInput();
    }
  };

  // Same as catalog onboarding: when a barcode is already known (scan miss), enrich immediately.
  useEffect(() => {
    if (!isWeight && initialBarcode) {
      runBarcodeLookup(initialBarcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once for the scanned barcode
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    if (!isSellingBelowMrp(sellingPrice, mrp)) {
      setError(SELLING_PRICE_MRP_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({
        sold_by: mode,
        barcode: isWeight ? undefined : barcode.trim(),
        name: name.trim(),
        image_url: imageUrl || undefined,
        selling_price: sellingPrice,
        mrp,
        qty,
      });
    } catch (err) {
      setError(err.message || 'Could not add this item.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUseExisting = async (product) => {
    if (submitting) return;
    const billQty = Number(qty);
    if (!Number.isFinite(billQty) || billQty <= 0 || (!isWeight && !Number.isInteger(billQty))) {
      setError(isWeight ? 'Enter a valid quantity in kg.' : 'Enter a whole-number pack quantity.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onUseExisting(product, billQty);
    } catch (err) {
      setError(err.message || 'Could not add this product.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="instant-onboard-title">
      <form className={styles.modal} onSubmit={submit}>
        <button type="button" className={styles.closeBtn} onClick={onClose} disabled={submitting} aria-label="Close">
          <X size={16} />
        </button>
        <h2 id="instant-onboard-title">{isWeight ? 'Add loose / weigh item' : 'Add packaged item'}</h2>
        <p className={styles.subtitle}>
          {isWeight
            ? 'Enter the rate per kg and the kg on this bill.'
            : 'Enter the pack details and quantity on this bill.'}
          {' '}No shelf stock will be added.
        </p>

        {enrichNotice && (
          <div className={`${styles.enrichBanner} ${styles[`enrich${enrichNotice.tone.charAt(0).toUpperCase()}${enrichNotice.tone.slice(1)}`]}`}>
            {enrichNotice.text}
          </div>
        )}

        {!isWeight && (
          <label className={styles.field}>
            <span>Barcode</span>
            <div className={styles.barcodeRow}>
              <input
                value={barcode}
                onChange={(event) => {
                  const next = event.target.value;
                  setBarcode(next);
                  setExistingMatch(null);
                  if (next.trim() !== lastLookupRef.current) setEnrichNotice(null);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  event.preventDefault();
                  runBarcodeLookup(barcode);
                }}
                inputMode="numeric"
                autoComplete="off"
                readOnly={Boolean(initialBarcode)}
                disabled={enriching}
                required
              />
              {enriching && <Loader2 size={16} className={styles.barcodeSpinner} aria-hidden />}
            </div>
            <small>
              {initialBarcode
                ? 'Scanned barcode'
                : enriching
                  ? 'Looking up product details…'
                  : 'Digits only — press Enter to look up name and image'}
            </small>
          </label>
        )}

        {(imageUrl || name) && !isWeight && (
          <div className={styles.enrichPreview}>
            {imageUrl ? (
              <img src={imageUrl} alt="" className={styles.enrichImage} />
            ) : (
              <div className={styles.enrichImagePlaceholder} aria-hidden />
            )}
            <div className={styles.enrichPreviewText}>
              <strong>{name || 'Product'}</strong>
              <span>{barcode.trim() || 'No barcode'}</span>
            </div>
          </div>
        )}

        {existingMatch && (
          <div className={styles.matches}>
            <p>Already in catalog</p>
            <div className={styles.matchRow}>
              <div>
                <strong>{existingMatch.name}</strong>
                <span>
                  {formatCurrency(existingMatch.price)}
                  {' · '}
                  {existingMatch.sku}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={submitting}
                onClick={() => handleUseExisting(existingMatch)}
              >
                Use existing
              </Button>
            </div>
          </div>
        )}

        <label className={styles.field}>
          <span>Product name</span>
          <input
            ref={nameInputRef}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
            autoFocus
            required
          />
        </label>

        {!existingMatch && matches.length > 0 && (
          <div className={styles.matches}>
            <p>Existing matches</p>
            {matches.map((product) => (
              <div key={product.id} className={styles.matchRow}>
                <div>
                  <strong>{product.name}</strong>
                  <span>
                    {formatCurrency(product.price)}
                    {isWeight ? ' / kg' : ''}
                    {' · '}
                    {product.catalogComplete === false ? 'Needs details' : product.sku}
                  </span>
                </div>
                <Button type="button" variant="outline" size="sm" disabled={submitting} onClick={() => handleUseExisting(product)}>
                  Use existing
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.priceRow}>
          <label className={styles.field}>
            <span>{isWeight ? 'Selling price / kg' : 'Selling price'}</span>
            <div className={styles.moneyInput}>
              <span>₹</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={sellingPrice}
                onChange={(event) => setSellingPrice(event.target.value)}
              />
            </div>
          </label>
          <label className={styles.field}>
            <span>{isWeight ? 'MRP / kg' : 'MRP'}</span>
            <div className={styles.moneyInput}>
              <span>₹</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={mrp}
                onChange={(event) => setMrp(event.target.value)}
              />
            </div>
          </label>
        </div>

        <label className={styles.field}>
          <span>{isWeight ? 'Bill quantity (kg)' : 'Bill quantity (packs)'}</span>
          <input
            type="number"
            min={isWeight ? '0.001' : '1'}
            step={isWeight ? '0.001' : '1'}
            value={qty}
            onChange={(event) => setQty(event.target.value)}
            required
          />
          {isWeight && <small>Enter kilograms, e.g. 0.650 for 650 g.</small>}
        </label>

        {error && <p className={styles.error}><AlertTriangle size={14} /> {error}</p>}

        {!matches.length && !existingMatch && debouncedName.length >= 2 && (
          <div className={styles.newHint}>
            <EmptyState
              icon={Search}
              title="No existing product"
              description="A POS stub will be created and marked Needs details."
            />
          </div>
        )}

        <div className={styles.actions}>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" disabled={submitting || enriching}>
            {submitting ? 'Adding…' : 'Create and add'}
          </Button>
        </div>
      </form>
    </div>
  );
}
